/**
 * Save-flow hook for the Comment RPA modal: payload construction,
 * API persistence, provider reassignment, and trail comments.
 *
 * @module commentRPAnewModal/hooks/useCommentRpaSave
 */
import { useSelector } from "react-redux";
import { markOrderAsCompleted } from "../../../../api/postCall/commentRpaModalSaveForOrderIds";
import { useBusniessOfficeNotesContext } from "../../../../context/busniessOfficeNotesContext";
import { usePayerNameContextFromSingleOrderLayout } from "../../../../context/payerNameContextFromSingleOrderLayout";
import { AllCommentsProps } from "../../../../data-model/busniessOfficeNote";
import { isExceptionFlowForUrologyOrg } from "../../../../enums/organizationTypes";
import { SingleOrderDetailsTableName } from "../../../../enums/singleOrderDetailsTableName";
import { useUpdateOrderDetails } from "../../../../hooks/singleMedicalPaOrderDetails/useUpdateOrderDetails";
import { runHappyFlowProviderReassignment } from "../../../../pages/singleOrderLayout/hooks/headerModals/utils/runHappyFlowProviderReassignment";
import { useAssignedToUpdate } from "../../../../pages/worklists/medicalPaOrdersAlgolia/hooks/useAssignedToUpdate";
import { RootState } from "../../../../redux/store/store";
import {
  insertCommentWritebackOncoemrTrailComment,
  insertDrugStatusFlippedTrailCommentIfChanged,
} from "../../../../utils/orderTrailComment";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import { getCommentsToWriteFromAllComments } from "../../../../utils/postCommentHelper";
import { showApiError } from "../../../../utils/showApiError";
import { ToastMessages } from "../../../toasts/toastMessages";
import { OrderReassignmentPayload } from "../../reAssignModal/types";
import {
  BuildCommentRpaSavePayloadParams,
  CommentRpaActingProviderDetails,
  CommentRpaSavePayloadResult,
  CommentRpaSaveRequestPayload,
  InsertCommentRpaPostSaveTrailCommentsParams,
  RunCommentRpaProviderReassignmentParams,
  UseCommentRpaSaveParams,
} from "../types";

/**
 * Builds the display name of the acting provider from Redux provider details.
 *
 * @param providerDetails - Provider slice from Redux
 * @returns Trimmed full name or a fallback label
 */
const getActingProviderDisplayName = (
  providerDetails?: CommentRpaActingProviderDetails | null,
): string => {
  return (
    `${providerDetails?.FirstName || ""} ${providerDetails?.LastName || ""}`.trim() ||
    "Unknown Provider"
  );
};

/**
 * Runs bulk or single-order provider reassignment when the modal has a selection.
 *
 * @param params - Form state, order context, and assignment runners
 */
const runCommentRpaProviderReassignmentIfSelected = async (
  params: RunCommentRpaProviderReassignmentParams,
): Promise<void> => {
  if (!params.formState.hasProviderSelection()) {
    return;
  }

  await runHappyFlowProviderReassignment({
    parentOrderId: params.orderId,
    selectedOrderIds: params.selectedOrderIds,
    selectedProvider: params.formState.selectedProvider.current,
    generalOrderDetails: params.generalOrderDetails,
    orgId: params.orgId,
    userEmail: params.userEmail,
    actingProviderName: params.actingProviderName,
    runBatchAssignmentUpdate: params.runBatchAssignmentUpdate,
    runUpdateGeneralOrderDetails: params.runUpdateGeneralOrderDetails,
  });
};

/**
 * Resolves write-back comments and builds the Comment RPA API payload.
 *
 * @param params - Org, form state, comments, and payer context
 * @returns Base payload, write-back slice, and BO status for trail comments
 */
const buildCommentRpaSavePayload = (
  params: BuildCommentRpaSavePayloadParams,
): CommentRpaSavePayloadResult => {
  const allPostComments = params.writeBackReports
    ? getCommentsToWriteFromAllComments(params.writeBackReports ?? [])
    : getCommentsToWriteFromAllComments(params.allCommentsForUI);

  const selectedBadgeId = params.formState.selectedBadge?.id || null;
  const isSunstateUrology = isExceptionFlowForUrologyOrg(params.orgId);
  const boStatusValue = isSunstateUrology ? null : selectedBadgeId;

  const writeBackReportsPayload =
    params.formState.shouldIncludeWriteBackReports &&
    allPostComments &&
    allPostComments.length > 0
      ? ((allPostComments ?? []) as unknown as AllCommentsProps[])
      : null;

  const basePayload: CommentRpaSaveRequestPayload = {
    org_id: params.orgId,
    overwrite_rpa_errors: true,
    write_back_reports: writeBackReportsPayload,
    ...(params.authStatus ? { auth_status: params.authStatus } : {}),
    payer_name: params.payerName,
    ...(isSunstateUrology
      ? { estimate_value: selectedBadgeId ?? undefined }
      : { bo_status: selectedBadgeId }),
  };

  return { basePayload, writeBackReportsPayload, boStatusValue };
};

/**
 * Inserts auth-status and write-back trail comments after a successful save.
 *
 * @param params - Order context, acting provider, and status/write-back deltas
 */
const insertCommentRpaPostSaveTrailComments = (
  params: InsertCommentRpaPostSaveTrailCommentsParams,
): void => {
  if (params.authStatus) {
    insertDrugStatusFlippedTrailCommentIfChanged({
      orderId: params.orderId,
      orgId: params.orgId,
      patientId: params.patientId,
      providerEmail: params.providerEmail,
      providerName: params.actingProviderName,
      fromStatusId: params.fromAuthStatusId,
      toStatusId: params.authStatus,
      authStatusOptions: params.authStatusOptions,
    });
  }

  if (
    params.writeBackReportsPayload &&
    params.writeBackReportsPayload.length > 0
  ) {
    insertCommentWritebackOncoemrTrailComment({
      orderId: params.orderId,
      orgId: params.orgId,
      patientId: params.patientId,
      providerEmail: params.providerEmail,
      providerName: params.actingProviderName,
      fromBoStatus: params.fromBoStatus,
      toBoStatus: params.boStatusValue ?? undefined,
      boOptions: params.boOptions,
    });
  }
};

/**
 * Encapsulates payload construction and parallel
 * {@link markOrderAsCompleted} API calls, followed by optional provider
 * reassignment (bulk when siblings are selected).
 *
 * @param params - Order context, form state, and callbacks
 * @returns The `handleSave` async handler
 */
export const useCommentRpaSave = ({
  orderId,
  formState,
  onSave,
  authStatus,
  authOnFileDetails,
  writeBackReports,
  generalOrderDetails,
}: UseCommentRpaSaveParams) => {
  const orgId = getOrgIdForFetchExternalWorklist();
  const { allCommentsForUI } = useBusniessOfficeNotesContext();
  const { payerName } = usePayerNameContextFromSingleOrderLayout();
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const { data: providerDetails } = useSelector(
    (state: RootState) => state.providerDetails,
  );
  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );
  const { data: boOptions } = useSelector(
    (state: RootState) => state.boOptions,
  );
  const { runUpdate: runUpdateGeneralOrderDetails } =
    useUpdateOrderDetails<OrderReassignmentPayload>({
      orderId,
      orgId,
      tableName: SingleOrderDetailsTableName.MEDICAL_PA_ORDER_GENERAL_INFO,
    });
  const { runUpdate: runBatchAssignmentUpdate } =
    useAssignedToUpdate<OrderReassignmentPayload>();

  const patientId = generalOrderDetails?.demographics?.patient_id ?? "";

  /**
   * Persists the RPA payload before optionally reassigning the provider,
   * then inserts trail comments.
   *
   * @param selectedOrderIds - Optional linked sibling order ids from the modal
   */
  const handleSave = async (selectedOrderIds?: string[]) => {
    try {
      formState.setIsLoading(true);

      const actingProviderName = getActingProviderDisplayName(providerDetails);

      const { basePayload, writeBackReportsPayload, boStatusValue } =
        buildCommentRpaSavePayload({
          orgId,
          formState,
          allCommentsForUI,
          writeBackReports,
          authStatus,
          payerName,
        });

      await markOrderAsCompleted(basePayload, orderId, selectedOrderIds);

      await runCommentRpaProviderReassignmentIfSelected({
        formState,
        orderId,
        selectedOrderIds,
        generalOrderDetails,
        orgId,
        userEmail: user?.email ?? "",
        actingProviderName,
        runBatchAssignmentUpdate,
        runUpdateGeneralOrderDetails,
      });

      insertCommentRpaPostSaveTrailComments({
        orderId,
        orgId,
        patientId,
        providerEmail: user?.email ?? "",
        actingProviderName,
        authStatus,
        fromAuthStatusId: authOnFileDetails?.auth_status,
        authStatusOptions: authStatusOptions ?? [],
        writeBackReportsPayload,
        boStatusValue,
        fromBoStatus: generalOrderDetails?.status?.bo_status,
        boOptions: boOptions ?? [],
      });

      onSave();
    } catch (error) {
      showApiError(error, ToastMessages.MARK_AS_COMPLETED_ERROR);
    } finally {
      formState.setIsLoading(false);
    }
  };

  return { handleSave };
};
